// src/services/adminApi.js

/**
 * Base JSON parsing function that checks for response.ok
 * and throws an error if not OK.
 * Handles 401 (Unauthorized) by clearing auth state and redirecting to login.
 */
async function fetchJSON(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      console.log("Unauthorized - clearing token and redirecting to login");
      localStorage.removeItem("memberstackToken");
      window.location.href = "/admin-login";
      throw new Error("Session expired. Redirecting to login...");
    }

    const errorData = await response.json().catch(() => ({}));
    console.log("error in adminApi is ", errorData);
    throw new Error(errorData.error || `Fetch error: ${response.status}`);
  }
  return response.json();
}

/**
 * Helper function to set up standard fetch options for admin routes
 * (e.g., credentials, authorization headers).
 */
async function doFetch(token, path, method = "GET", body = null) {
  const options = {
    method,
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (body) {
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }

  return fetchJSON(path, options);
}

/**
 * Helper for building a query string from a filters object.
 * e.g. buildQueryString({ foo: "bar", expand: "1" }) => "?foo=bar&expand=1"
 */
function buildQueryString(filters) {
  if (!filters || Object.keys(filters).length === 0) {
    return "";
  }
  return "?" + new URLSearchParams(filters).toString();
}

export const adminApi = {
  // GATHERINGS
  getGatherings: async (filters = {}, token) => {
    // Always expand venue data
    const updatedFilters = { ...filters, expand: "1" };
    const qs = buildQueryString(updatedFilters);
    return doFetch(token, `/admin/gatherings${qs}`);
  },

  updateGathering: async (gatheringId, updates, token) => {
    return doFetch(token, `/admin/gatherings/${gatheringId}`, "PUT", updates);
  },

  // VENUES
  getVenuesWithLocation: async (token, filters = {}) => {
    const qs = buildQueryString(filters);
    return doFetch(token, `/admin/venues${qs}`);
  },

  createVenue: async (newVenue, token) => {
    return doFetch(token, `/admin/venues`, "POST", newVenue);
  },

  updateVenue: async (venueId, updatedVenue, token) => {
    return doFetch(token, `/admin/venues/${venueId}`, "PUT", updatedVenue);
  },

  deleteVenue: async (venueId, token) => {
    return doFetch(token, `/admin/venues/${venueId}`, "DELETE");
  },

  // LOCATIONS
  getLocations: async (token) => {
    return doFetch(token, `/admin/locations`);
  },

  createLocation: async (locationData, token) => {
    return doFetch(token, `/admin/locations`, "POST", locationData);
  },

  updateLocation: async (locationId, updates, token) => {
    return doFetch(token, `/admin/locations/${locationId}`, "PUT", updates);
  },

  deleteLocation: async (locationId, token) => {
    return doFetch(token, `/admin/locations/${locationId}`, "DELETE");
  },

  // SITE CONTENT (admin-editable HTML, e.g. My Gatherings FAQ)
  getSiteContent: async (contentKey) => {
    const response = await fetch(`/api/site-content/${encodeURIComponent(contentKey)}`);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || response.statusText);
    }
    return response.json();
  },

  updateSiteContent: async (contentKey, contentHtml, token) => {
    return doFetch(token, `/admin/site-content/${encodeURIComponent(contentKey)}`, "PUT", { content_html: contentHtml });
  },

  // THEME LOCATION CONFIG (per-location venue + schedule overrides)
  getThemeLocationVenues: async (themeId, token) => {
    return doFetch(token, `/admin/theme-location-venues/${themeId}`);
  },

  updateThemeLocationVenues: async (themeId, venues, token) => {
    return doFetch(token, `/admin/theme-location-venues/${themeId}`, "PUT", { venues });
  },

  // USER-GATHERINGS
  getUserGatherings: async (filters = {}, token) => {
    const qs = buildQueryString(filters);
    return doFetch(token, `/admin/user-gatherings${qs}`);
  },

  // USER-SLOTS
  getUserSlots: async (filters = {}, token) => {
    const qs = buildQueryString(filters);
    return doFetch(token, `/admin/user-slots${qs}`);
  },

  // GATHERINGS SUMMARY
  getGatheringsSummary: async (filters = {}, token) => {
    const qs = buildQueryString(filters);
    return doFetch(token, `/admin/gatherings-summary${qs}`);
  },

  // UPDATE GATHERING DETAILS
  updateGatheringDetails: async (gatheringId, updates, token) => {
    return doFetch(token, `/admin/gatherings/${gatheringId}`, "PUT", updates);
  },

  // USERS
  getUsers: async (token) => {
    return doFetch(token, `/admin/users`);
  },

  // GET /admin/members-with-plans?memberIds=mem_abc,mem_def&include=planConnections
  getMembersWithPlans: async (token) => {
    return doFetch(token, `/admin/members-with-plans`);
  },

  // SUMMARY
  getSummary: async (token) => {
    return doFetch(token, `/admin/summary`);
  },

  // SUBSCRIPTIONS (Stripe, including canceled — for acquisition tracking)
  getSubscriptions: async (token) => {
    return doFetch(token, `/api/admin/subscriptions`);
  },

  // Live Stripe scan for failed/high-risk charges - only called when the
  // admin clicks "Load" on the Payment Issues tab, not on tab open.
  getPaymentIssues: async (token, days = 7) => {
    return doFetch(token, `/api/admin/payments/issues?days=${days}`);
  },

  // Pulls a fresh Stripe scan AND sends an anonymized summary to Claude for
  // analysis, in one request - matches the other "Analyze with Claude"
  // buttons (always available, not gated behind a separate load step).
  analyzePaymentIssues: async (token, days = 7) => {
    return doFetch(token, `/api/admin/payments/analyze?days=${days}`, "POST");
  },

  // Cheap poll target while a background Meetup sync is running - doesn't
  // hit Stripe or re-trigger anything, just reads current status.
  getMeetupSyncStatus: async (token) => {
    return doFetch(token, `/api/admin/subscriptions/meetup-sync-status`);
  },

  // Site-wide page views bucketed by day-of-week, from Mixpanel - powers
  // the Subscriptions tab's views/new-subscriptions/conversion-by-weekday
  // chart. fromDate/toDate are "YYYY-MM-DD"; omit either to use the
  // backend's default (last 30 days).
  getViewsByWeekday: async (token, fromDate, toDate) => {
    const query = buildQueryString({
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    });
    return doFetch(token, `/api/admin/subscriptions/views-by-weekday${query}`);
  },

  // Maps a raw Stripe source_group referral tag (e.g. "birmingham20s30s")
  // to the real Meetup group it refers to (e.g.
  // "birmingham-socials-20s-30s") when the two were written differently -
  // see models.SourceGroupMapping. Re-runs auto-matching on every call, so
  // this scans Stripe - not something to call on every render.
  getSourceGroupMappings: async (token) => {
    return doFetch(token, `/api/admin/source-group-mappings`);
  },

  // Cheap, DB-only count of mappings still needing admin review - safe to
  // call from other tabs (Subscriptions, Meetup Group Finance) just to show
  // a "N need mapping" callout, since it doesn't scan Stripe or re-match.
  getSourceGroupMappingsNeedsReview: async (token) => {
    return doFetch(token, `/api/admin/source-group-mappings/needs-review-summary`);
  },

  updateSourceGroupMapping: async (sourceGroup, action, meetupUrlname, token) => {
    return doFetch(
      token, `/api/admin/source-group-mappings/${encodeURIComponent(sourceGroup)}`, "PATCH",
      { action, ...(meetupUrlname !== undefined ? { meetup_urlname: meetupUrlname } : {}) },
    );
  },

  // THEME LANDING PAGES
  getThemeLandingPages: async (token) => {
    return doFetch(token, `/api/admin/theme-landing-pages`);
  },

  createThemeLandingPage: async (pageData, token) => {
    return doFetch(token, `/api/admin/theme-landing-pages`, "POST", pageData);
  },

  verifyMeetupGroup: async (urlname, token) => {
    return doFetch(token, `/api/admin/meetup/verify-group`, "POST", { urlname });
  },

  updateThemeLandingPage: async (pageId, updates, token) => {
    return doFetch(token, `/api/admin/theme-landing-pages/${pageId}`, "PUT", updates);
  },

  deleteThemeLandingPage: async (pageId, token) => {
    return doFetch(token, `/api/admin/theme-landing-pages/${pageId}`, "DELETE");
  },

  getLatestLandingPageReview: async (token) => {
    return doFetch(token, `/api/admin/theme-landing-pages/review`);
  },

  createLandingPageReview: async (token) => {
    return doFetch(token, `/api/admin/theme-landing-pages/review`, "POST");
  },

  // GATHERING UNAVAILABILITY
  getGatheringUnavailability: async (token) => {
    return doFetch(token, `/api/admin/gathering-unavailability`);
  },

  createGatheringUnavailability: async (unavailabilityData, token) => {
    return doFetch(token, `/api/admin/gathering-unavailability`, "POST", unavailabilityData);
  },

  deleteGatheringUnavailability: async (recordId, token) => {
    return doFetch(token, `/api/admin/gathering-unavailability/${recordId}`, "DELETE");
  },

  // EMAIL CAMPAIGNS
  getEmailCampaigns: async (token) => {
    return doFetch(token, `/api/admin/email-campaigns`);
  },

  getEmailCampaign: async (campaignId, token) => {
    return doFetch(token, `/api/admin/email-campaigns/${campaignId}`);
  },

  createEmailCampaign: async (campaignData, token) => {
    return doFetch(token, `/api/admin/email-campaigns`, "POST", campaignData);
  },

  updateEmailCampaign: async (campaignId, updates, token) => {
    return doFetch(token, `/api/admin/email-campaigns/${campaignId}`, "PUT", updates);
  },

  deleteEmailCampaign: async (campaignId, token) => {
    return doFetch(token, `/api/admin/email-campaigns/${campaignId}`, "DELETE");
  },

  getEmailCampaignAnalytics: async (campaignId, token) => {
    return doFetch(token, `/api/admin/email-campaigns/${campaignId}/analytics`);
  },

  previewEmailCampaignAudience: async (campaignId, token) => {
    return doFetch(token, `/api/admin/email-campaigns/${campaignId}/preview`);
  },
};
